import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/index.js';

/**
 * Live inbox for the current operator. Fetches the initial list via REST,
 * subscribes to /ws/inbox for new notifications, and keeps the unread count
 * in sync.
 *
 * Returns:
 *   { connected, items, unreadCount, refresh, markRead, dismiss, markAllRead }
 */
export function useInbox({ enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const backoffMs = useRef(1000);
  const stoppedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.notifications.inbox({ limit: 50 });
      setItems(r.items || []);
      setUnreadCount(r.unreadCount ?? 0);
    } catch {
      /* ignore — keep last known state */
    }
  }, []);

  const markRead = useCallback(async (id) => {
    await api.notifications.markRead(id);
    setItems((prev) =>
      prev.map((n) => (n.id === id
        ? { ...n, delivery: { ...(n.delivery || {}), status: 'read', readAt: new Date().toISOString() } }
        : n)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const dismiss = useCallback(async (id) => {
    await api.notifications.dismiss(id);
    setItems((prev) => prev.filter((n) => n.id !== id));
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.notifications.markAllRead();
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    stoppedRef.current = false;
    refresh();

    function open() {
      if (stoppedRef.current) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/ws/inbox`;
      let ws;
      try { ws = new WebSocket(url); } catch { scheduleReconnect(); return; }
      wsRef.current = ws;

      ws.onopen = () => { backoffMs.current = 1000; setConnected(true); };
      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg.type === 'hello') {
          setUnreadCount(msg.unreadCount ?? 0);
        } else if (msg.type === 'notification.created') {
          // Re-fetch to get the canonical delivery state for our user.
          refresh();
        } else if (msg.type === 'notification.retracted') {
          setItems((prev) => prev.filter((n) => n.id !== msg.id));
          refresh();
        } else if (msg.type === 'ping') {
          try { ws.send(JSON.stringify({ type: 'pong' })); } catch { /* ignore */ }
        }
      };
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        scheduleReconnect();
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    }

    function scheduleReconnect() {
      if (stoppedRef.current) return;
      const delay = backoffMs.current;
      backoffMs.current = Math.min(backoffMs.current * 2, 15_000);
      reconnectTimer.current = setTimeout(open, delay);
    }

    open();

    return () => {
      stoppedRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(1000, 'unmount'); } catch { /* ignore */ }
      }
      wsRef.current = null;
    };
  }, [enabled, refresh]);

  return { connected, items, unreadCount, refresh, markRead, dismiss, markAllRead };
}
